import { useCallback, useMemo } from 'react';

import {
  Box,
  BoxProps,
  CloseButton,
  Divider,
  Flex,
  Link,
  Text,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';

import {
  EXPLORE_STAKING,
  GOVERNANCE_OPTION_CLICK,
  JOIN_OUR_COMMUNITY,
  STADER_LOGO,
} from '@/constants/analytics';
import { LinkOption } from '@/customtypes/global';
import { trackEvent, useDappSDKProvider } from '@/dapp-sdk-v2';
import BaseImage from '@/dapp-sdk-v2/components/Image';
import { useCMSData } from '@/providers/CMSDataProvider';
import { SidebarContainer } from '@/themes';
import { getLandingUrl, getQueryString } from '@/utils/common';

import Governance from './Governance';
import LiquidStaking from './LiquidStaking';
import Options from './Options';
import Socials from './Socials';

interface SidebarProps extends BoxProps {
  isEmbed?: boolean;
  setMobileMenuVisible?: (isVisible: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  setMobileMenuVisible,
  isEmbed = false,
  ...rest
}) => {
  const router = useRouter();
  const { isSafeApp } = useDappSDKProvider();

  // const pageDataFromContext = usePageData('$.page.data')[0] || {};

  // const pageData = useMemo(() => {
  //   if (pageDataFromProps != null) {
  //     return pageDataFromProps;
  //   }
  //   return pageDataFromContext;
  // }, [pageDataFromContext, pageDataFromProps]);

  const pageData = useCMSData((data) => data.page);

  const {
    liquid_staking_options = {
      data: [],
    },
    community_section,
    governance = {
      data: [],
    },
  } = pageData;

  const { sidebar } = pageData;

  const { query } = router;
  const queryString = getQueryString(query);

  const liquidStakingOptions: LinkOption[] = useMemo(() => {
    const options = liquid_staking_options?.data;
    return options?.map((data) => data?.attributes || []) ?? [];
  }, [liquid_staking_options]);

  const governanceOptions: LinkOption[] = useMemo(() => {
    const options = governance?.data || [];
    return options.map((data: LinkOption) => data || {});
  }, [governance]);

  const activeMenu = useMemo(() => {
    const pathArray = router.pathname.split('/');
    const [, subMenu] = pathArray;
    return {
      subMenu: subMenu,
    };
  }, [router.pathname, sidebar]);

  const optionsItemClick = useCallback((key: string) => {
    let eventName = '';
    switch (key) {
      case 'forum': {
        eventName = 'SD_community_forum';
        break;
      }
      case 'sdDetails': {
        eventName = 'SD_details';
        break;
      }
    }
    if (eventName) {
      trackEvent(eventName, {});
    }
    setMobileMenuVisible && setMobileMenuVisible(false);
  }, []);

  const socialsClick = useCallback((key: string) => {
    trackEvent(JOIN_OUR_COMMUNITY, {
      joc_type: key,
    });
  }, []);

  const exploreStakingClick = useCallback((key: string) => {
    trackEvent(EXPLORE_STAKING, {
      next_blockchain: key,
    });
  }, []);

  const governanceClick = useCallback((key: string) => {
    trackEvent(GOVERNANCE_OPTION_CLICK, {
      key: key,
    });
  }, []);

  return (
    <SidebarContainer {...rest} overflowY="auto">
      <Flex
        pt={2}
        flexDir="column"
        h="100%"
        justifyContent={{ base: 'flex-start', xl: 'space-between' }}
      >
        <Box mb="2.5rem">
          <Flex
            alignItems="center"
            gap="0.5rem"
            mb={{ base: '2.5rem', xl: '4.5rem' }}
            justifyContent={{ base: 'space-between', xl: 'flex-start' }}
          >
            <Link
              href={`${getLandingUrl()}${queryString ? `?${queryString}` : ''}`}
              height="24px"
              onClick={() => {
                trackEvent(STADER_LOGO, {});
              }}
              _hover={{
                textDecor: 'none',
              }}
            >
              <Flex
                gap="4px"
                alignItems="center"
                pointerEvents={isEmbed || isSafeApp ? 'none' : 'all'}
              >
                <BaseImage
                  src="/assets/images/stader-icon.svg"
                  width={40}
                  height={40}
                  alt="stader-logo"
                />
                <Text fontWeight="600" fontSize="24px" color="textPrimary">
                  Stader
                </Text>
              </Flex>
            </Link>
            <CloseButton
              display={{ base: 'flex', xl: 'none' }}
              onClick={() =>
                setMobileMenuVisible && setMobileMenuVisible(false)
              }
            />
          </Flex>

          <Options
            sidebarData={sidebar}
            activeMenuKey={activeMenu.subMenu}
            onClick={optionsItemClick}
            title=""
            isEmbed={isEmbed}
          />

          {!(isEmbed || isSafeApp) && (
            <Box mt="20px">
              <LiquidStaking
                blockChains={liquidStakingOptions || []}
                onClick={exploreStakingClick}
              />
            </Box>
          )}

          {!(isEmbed || isSafeApp) && governanceOptions?.length > 0 ? (
            <Box mt="20px">
              <Governance
                governanceData={governanceOptions || []}
                onClick={governanceClick}
              />
            </Box>
          ) : null}
        </Box>

        <Box>
          <Divider
            color="seperator"
            display={{ base: 'block', xl: 'none' }}
            border="1px solid"
            opacity="1"
            borderColor="seperator"
            borderBottomWidth="0"
          />

          {community_section && (
            <Socials socialItems={community_section} onClick={socialsClick} />
          )}
        </Box>
      </Flex>
    </SidebarContainer>
  );
};

export default Sidebar;
